/**
 * KYC Detail - Minimal RTL Design
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CloseIcon from '@mui/icons-material/Close';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import kycService from '../services/kyc.service';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';

import './KycDetail.css';

const statusLabels: Record<string, string> = {
  pending: 'در حال بررسی',
  approved: 'تایید شده',
  rejected: 'رد شده'
};

const KycDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [row, setRow] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await kycService.getById(id!);
      if (res?.status === 'success' && res.data) {
        setRow(res.data);
      } else if (res && (res._id || res.userId)) {
        setRow(res);
      } else {
        setError((res as any)?.message || 'خطا در دریافت جزئیات');
      }
    } catch (e: any) {
      setError(e?.message || 'خطا در دریافت جزئیات');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async () => {
    try {
      await kycService.approve(id!);
      navigate('/kyc');
    } catch (e: any) {
      setError(e?.message || 'خطا در تایید');
    }
  };

  const reject = async () => {
    const reason = prompt('دلیل رد را وارد کنید:') || '';
    if (!reason.trim()) return;
    try {
      await kycService.reject(id!, reason);
      navigate('/kyc');
    } catch (e: any) {
      setError(e?.message || 'خطا در رد');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleZoomIn = () => setZoom(z => Math.min(4, z + 0.5));
  const handleZoomOut = () => setZoom(z => Math.max(0.5, z - 0.5));
  const handleClosePreview = () => {
    setPreviewSrc(null);
    setZoom(1);
  };

  if (loading) {
    return (
      <div className="kyc-detail-page">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className="kyc-detail-page">
      {/* Header */}
      <header className="kyc-detail-header">
        <h1><VerifiedUserIcon /> جزئیات احراز هویت</h1>
        <button className="back-btn" onClick={() => navigate('/kyc')}>
          <ArrowForwardIcon fontSize="small" />
          بازگشت به لیست
        </button>
      </header>

      {/* Error Alert */}
      {error && (
        <div className="kyc-alert error">
          {error}
        </div>
      )}

      {row && (
        <>
          {/* Info Card */}
          <div className="kyc-info-card">
            <div className="kyc-info-grid">
              <div className="kyc-info-item">
                <span className="kyc-info-label">نام کاربر</span>
                <span className="kyc-info-value">{row.user?.name || row.name || '-'}</span>
              </div>
              <div className="kyc-info-item">
                <span className="kyc-info-label">ایمیل</span>
                <span className="kyc-info-value">{row.user?.email || row.email || '-'}</span>
              </div>
              <div className="kyc-info-item">
                <span className="kyc-info-label">نام (KYC)</span>
                <span className="kyc-info-value">{row.firstName || '-'}</span>
              </div>
              <div className="kyc-info-item">
                <span className="kyc-info-label">نام خانوادگی (KYC)</span>
                <span className="kyc-info-value">{row.lastName || '-'}</span>
              </div>
              <div className="kyc-info-item">
                <span className="kyc-info-label">شناسه کاربر</span>
                <span className="kyc-info-value">{row.user?._id || row.userId || '-'}</span>
              </div>
              <div className="kyc-info-item">
                <span className="kyc-info-label">شماره ملی</span>
                <span className="kyc-info-value">{row.nationalIdNumber || '-'}</span>
              </div>
              <div className="kyc-info-item">
                <span className="kyc-info-label">وضعیت</span>
                <span className={`status-badge ${row.status}`}>
                  {statusLabels[row.status] || row.status}
                </span>
              </div>
              <div className="kyc-info-item">
                <span className="kyc-info-label">تاریخ ارسال</span>
                <span className="kyc-info-value">{formatDate(row.submittedAt)}</span>
              </div>
              {row.reviewedAt && (
                <div className="kyc-info-item">
                  <span className="kyc-info-label">تاریخ بررسی</span>
                  <span className="kyc-info-value">{formatDate(row.reviewedAt)}</span>
                </div>
              )}
              {row.rejectReason && (
                <div className="kyc-info-item" style={{ gridColumn: '1 / -1' }}>
                  <span className="kyc-info-label">دلیل رد</span>
                  <span className="kyc-info-value error">{row.rejectReason}</span>
                </div>
              )}
            </div>
          </div>

          {/* Images Section */}
          <div className="kyc-images-section">
            <h3 className="kyc-images-title">تصاویر مدارک</h3>
            <div className="kyc-images-grid">
              {/* Front Image */}
              <div className="kyc-image-card">
                <div className="kyc-image-label">روی کارت ملی</div>
                <div
                  className="kyc-image-container"
                  onClick={() => row.frontImageBase64 && setPreviewSrc(row.frontImageBase64)}
                >
                  {row.frontImageBase64 ? (
                    <img src={row.frontImageBase64} alt="روی کارت ملی" />
                  ) : (
                    <div className="kyc-image-placeholder">
                      <NoPhotographyIcon />
                      <p>تصویری آپلود نشده</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Back Image */}
              <div className="kyc-image-card">
                <div className="kyc-image-label">پشت کارت ملی</div>
                <div
                  className="kyc-image-container"
                  onClick={() => row.backImageBase64 && setPreviewSrc(row.backImageBase64)}
                >
                  {row.backImageBase64 ? (
                    <img src={row.backImageBase64} alt="پشت کارت ملی" />
                  ) : (
                    <div className="kyc-image-placeholder">
                      <NoPhotographyIcon />
                      <p>تصویری آپلود نشده</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Selfie Image */}
              <div className="kyc-image-card">
                <div className="kyc-image-label">تصویر سلفی</div>
                <div
                  className="kyc-image-container"
                  onClick={() => row.selfieImageBase64 && setPreviewSrc(row.selfieImageBase64)}
                >
                  {row.selfieImageBase64 ? (
                    <img src={row.selfieImageBase64} alt="سلفی" />
                  ) : (
                    <div className="kyc-image-placeholder">
                      <NoPhotographyIcon />
                      <p>تصویری آپلود نشده</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Shift / category eligibility banner */}
          {row.status === 'pending' && <MyShiftStatusBanner category="kyc" />}

          {/* Actions */}
          {row.status === 'pending' && (
            <div className="kyc-actions">
              <button className="kyc-action-btn approve" onClick={approve}>
                <CheckCircleIcon fontSize="small" />
                تایید احراز هویت
              </button>
              <button className="kyc-action-btn reject" onClick={reject}>
                <CancelIcon fontSize="small" />
                رد درخواست
              </button>
            </div>
          )}

          {row.status !== 'pending' && (
            <div className="kyc-actions">
              <button className="kyc-action-btn back" onClick={() => navigate('/kyc')}>
                <ArrowForwardIcon fontSize="small" />
                بازگشت به لیست
              </button>
            </div>
          )}
        </>
      )}

      {/* Image Preview Overlay */}
      {previewSrc && (
        <div className="image-preview-overlay" onClick={handleClosePreview}>
          <div className="image-preview-header" onClick={(e) => e.stopPropagation()}>
            <button className="preview-control-btn" onClick={handleZoomOut} title="کوچک‌نمایی">
              <ZoomOutIcon />
            </button>
            <button className="preview-control-btn" onClick={handleZoomIn} title="بزرگ‌نمایی">
              <ZoomInIcon />
            </button>
            <button className="preview-control-btn" onClick={handleClosePreview} title="بستن">
              <CloseIcon />
            </button>
          </div>
          <div className="image-preview-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={previewSrc}
              alt="پیش‌نمایش"
              style={{ transform: `scale(${zoom})` }}
              onClick={() => setZoom(z => z >= 2 ? 1 : z + 0.5)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default KycDetail;
